import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article: Article | undefined = id
    ? articles.find((a) => a.id === parseInt(id))
    : undefined;

  const relatedArticles = articles
    .filter((a) => a.id !== article?.id && a.category === article?.category)
    .slice(0, 3);

  if (id === 'latest') {
    return <ArticleListPage />;
  }

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您要查看的文章不存在或已被移除</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '资讯文章', path: '/article/latest' },
                { label: article.title },
              ]}
            />

            <header className="mt-4 mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs bg-primary-100 text-primary-700 px-3 py-1 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约{article.readTime}
                </span>
              </div>
            </header>

            <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8 bg-background-100">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                相关文章推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group block cursor-pointer"
                  >
                    <div className="aspect-[16/10] rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <h4 className="font-medium text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {item.title}
                    </h4>
                    <span className="text-xs text-foreground-400 mt-1 block">{item.date}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>

        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.image,
            "author": { "@type": "Person", "name": article.author },
            "datePublished": article.date,
            "publisher": { "@type": "Organization", "name": "91影视看片app" },
            "url": `https://www.91yingshi.com/article/${article.id}`
          })
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.91yingshi.com/" },
              { "@type": "ListItem", "position": 2, "name": "资讯文章", "item": "https://www.91yingshi.com/article/latest" },
              { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://www.91yingshi.com/article/${article.id}` }
            ]
          })
        }} />
      </main>
      <Footer />
    </>
  );
}

function ArticleListPage() {
  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '资讯文章' },
              ]}
            />

            <div className="mt-4 mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                影视资讯
              </h1>
              <p className="text-foreground-600 text-sm">
                观影指南、版本更新、使用技巧，掌握最新影视动态
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <article key={article.id}>
                  <Link
                    to={`/article/${article.id}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 hover:translate-y-[-2px] cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs bg-primary-100 text-primary-700 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                          {article.category}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                      </div>
                      <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h2>
                      <p className="text-foreground-600 text-sm line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="mt-3 flex items-center gap-1 text-primary-600 text-sm font-medium">
                        <span>阅读全文</span>
                        <i className="ri-arrow-right-line text-xs group-hover:translate-x-1 transition-transform duration-200"></i>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "影视资讯 - 91影视看片app",
            "description": "91影视看片app最新影视资讯文章列表，包含观影指南、版本更新、使用技巧等实用内容。",
            "url": "https://www.91yingshi.com/article/latest",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.91yingshi.com/" },
                { "@type": "ListItem", "position": 2, "name": "资讯文章", "item": "https://www.91yingshi.com/article/latest" }
              ]
            }
          })
        }} />
      </main>
      <Footer />
    </>
  );
}